import ProductAttributesApiV2 from 'api/v2/admins/product-attributes-api-v2'
import ProductCategoriesApiV2 from 'api/v2/admins/product-categories-api-v2'
import LoaderButton from 'components/molecules/loader-button'
import ConfirmationModal from 'components/organisms/confirmation-modal'
import { CODE } from 'domains/product-attribute/constants'
import ProductAttributeService from 'domains/product-attribute/services'
import ProductCategorySelectField from 'domains/product-category/molecules/select-field'
import ScraperProductService from 'domains/scraper-product/services/scraper-product'
import { Form, Formik } from 'formik'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import CurateAcceptScraperProductAttributeFields from './curate-accept-scraper-product-attribute-fields'
import defaultSchema from './scraper-product-curate-accept-schema'


const propTypes = {
  handleSubmit   : PropTypes.func,
  scraperProduct : PropTypes.object,
  gender         : PropTypes.string
}

// * In Scraper Product Curation form, we don't want to input quantity
const EXCLUDED_ATTRIBUTE_KEYS = [
  CODE.QUANTITY,
  CODE.CATEGORY
]

const DEFAULT_MODAL_MESSAGE = 'Are you sure want to accept the product?'

// TODO: add size to Product Attribute in the database. so we don't need to hardcoded size initial value here
const defaultInitialValues = {
  category : '',
  size     : ''
}

/**
 * Curate accept product for Scraper Product.
 * @param {func} handleSubmit - Handler to submit curation accept form
 * @param {object} scraperProduct - Scraper Product data needed to be accept
 */
const ScraperProductCurateAcceptForm = ({
  scraperProduct,
  handleSubmit,
  gender
}) => {
  const [ reinitializeForm, setReinitializeForm ] = useState(false)
  const [ attributes, setAttributes ]             = useState([])
  const [ sizes, setSizes ]                       = useState([])

  const [ initialValues, setInitialValues ]                     = useState(defaultInitialValues)
  const [ dynamicValidationSchema, setDynamicValidationSchema ] = useState(defaultSchema(sizes))
  const [ isModalOpen, setIsModalOpen ]                         = useState(false)
  const [ modalMessage, setModalMessage ]                       = useState(DEFAULT_MODAL_MESSAGE)

  /** Toggle for Confirmation modal */
  const toggleAcceptModal = () => setIsModalOpen((prev) => !prev)

  /**
   * Generate message for the confirmation modal
   * @param {object} value - Formik's values
   */
  const generateMessageModal = (values) => {
    const { price } = values

    price < 50000
      ? setModalMessage(`The price of the product is less than Rp. 50,000. ${DEFAULT_MODAL_MESSAGE}`)
      : setModalMessage(DEFAULT_MODAL_MESSAGE)
  }

  // * using useCallback to prevent the component always re-rendering
  const onFetchAttributes = (category_id) => {
    ProductAttributesApiV2.getCurationAttributes(category_id)
      .then((response) => {
        // * exclude particular attributes
        const filteredAttributes = response.data.attributes.filter((attribute) => !EXCLUDED_ATTRIBUTE_KEYS.includes(attribute.code))

        var buying_price_attribute = {
          allow_multiple_values   : false,
          code                    : 'buying_price',
          context                 : 'system',
          curation_field_required : true,
          curation_text           : null,
          datatype                : 'price',
          has_reference_table     : false,
          id                      : 27,
          is_curation_field       : true,
          label                   : 'Harga',
          order                   : 10,
          unit                    : 'rupiah'
        }

        filteredAttributes.push(buying_price_attribute)

        setAttributes(filteredAttributes)
        setReinitializeForm(true)
      })
      .catch((error) => {
        console.log(error)
      })
  }

  // * using useCallback to prevent the component always re-rendering
  const onFetchSizes = (category_id) => {
    ProductCategoriesApiV2.getSizes(category_id)
      .then((response) => {

        setSizes(response.data.sizes)
      })
      .catch((error) => {
        console.log(error)
      })
  }

  /**
   * Handle logics everytime category has changed
   * - refetching sizes depend on `category_id`
   * - refetching attributes depend `on category_id`
   * - overwrite `category_id` in `initialValues`
   */
  const handleChangeCategory = useCallback((category_id) => {
    onFetchSizes(category_id)
    onFetchAttributes(category_id)

    // * Set initial values of weight in the form
    const weightDefaultValue = ScraperProductService.getDefaultWeightAttributeValue(category_id)

    // * Change category and weight with current value
    // * We still keep the other values
    setInitialValues((prev) => ({
      ...prev,
      category : category_id,
      weight   : weightDefaultValue
    }))

  }, [])

  useEffect(() => {
    // * Set initial values of the form
    // * If scraper product props exists, it will set the value to the fields (for Edit Curation case)
    const currentValues = scraperProduct
      ? ScraperProductService.getAcceptedProductAttributeValues(scraperProduct)
      : defaultInitialValues

    setInitialValues(currentValues)
  }, [ scraperProduct ])

  useEffect(() => {

    if (reinitializeForm) {
      // * Two things happen when reinitialized the form
      // * - generate new validation schema
      // * - build new initial values. It is needed in order to trigger validation

      const attributesSchema          = ProductAttributeService.buildFormikValidationSchema(attributes)
      const generatedValidationSchema = defaultSchema(sizes).concat(attributesSchema)

      const newInitialValues = ProductAttributeService.buildFormikInitialValue(
        attributes,
        // * to add Size default value
        { size: '', ...initialValues }
      )

      setDynamicValidationSchema(generatedValidationSchema)
      setInitialValues(newInitialValues)

      // * to make sure React stop to re-render
      setReinitializeForm(false)
    }

  }, [ reinitializeForm, sizes, attributes, initialValues ])

  return (
    <Formik
      enableReinitialize={ true }
      initialValues={ initialValues }
      validationSchema={ dynamicValidationSchema }
      onSubmit={ (value, formikBag) => {
        formikBag.setSubmitting(false)
        generateMessageModal(value)
        toggleAcceptModal()
      } }
    >
      {
        (formikBag) => (
          <Form>
            <ProductCategorySelectField
              name='category'
              label='Category'
              required={ true }
              gender={ gender }
            />

            <CurateAcceptScraperProductAttributeFields
              attributes={ attributes }
              sizes={ sizes }
              onChangeCategory={ handleChangeCategory }
            />

            <LoaderButton
              color='primary'
              type='submit'
              className='float-right'
              onSubmit={ formikBag.submitForm }
              disabled={ formikBag.isSubmitting }
              isLoading={ formikBag.isSubmitting }
              text='Curate'
            />

            <ConfirmationModal
              isOpen={ isModalOpen }
              toggle={ toggleAcceptModal }
              title={ 'Accept the Product' }
              handleConfirmation={ () => {
                handleSubmit(formikBag.values, attributes, formikBag)
                toggleAcceptModal()
              } }
            >
              { modalMessage }
            </ConfirmationModal>

          </Form>
        )
      }
    </Formik>
  )
}

ScraperProductCurateAcceptForm.propTypes = propTypes

export default ScraperProductCurateAcceptForm
